
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/lib/toast';

export interface KanbanCard {
  id: string;
  title: string;
  description?: string;
  column_id: string;
  position: number;
  due_date?: string;
  assignee?: string;
  assignee_avatar?: string;
  priority?: 'low' | 'medium' | 'high';
  created_at: string;
  updated_at: string;
  kanban_columns?: {
    name: string;
    kanban_boards?: {
      name: string;
      user_id: string;
      clients?: {
        name: string;
      };
    };
  };
}

export interface TaskItem {
  id: string;
  title: string;
  dueDate: string;
  status: string;
  client?: string;
}

export interface RecentActivity {
  id: string;
  action: string;
  card_title: string;
  board_name?: string;
  client_name?: string;
  user_name?: string;
  created_at: string;
  user: {
    name: string;
    initials: string;
  };
  target: string;
  client?: string;
  formattedTime: string;
  icon: string;
  iconColor: string;
}

export function useTasks() {
  const [tasks, setTasks] = useState<KanbanCard[]>([]);
  const [recentActivities, setRecentActivities] = useState<RecentActivity[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchTasks = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('kanban_cards')
        .select(`
          id,
          title,
          description,
          column_id,
          position,
          due_date,
          assignee,
          assignee_avatar,
          created_at,
          updated_at,
          kanban_columns (
            name,
            kanban_boards (
              name,
              user_id,
              clients (
                name
              )
            )
          )
        `)
        .eq('is_archived', false)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Filter only tasks from user's boards
      const userTasks = (data || []).filter(task => 
        task.kanban_columns?.kanban_boards?.user_id === user.id
      );

      setTasks(userTasks as KanbanCard[]);
    } catch (error: any) {
      console.error('Error fetching tasks:', error);
      toast.error("Erro ao carregar tarefas");
    }
  };

  const fetchRecentActivities = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Get recent cards from user's boards
      const { data, error } = await supabase
        .from('kanban_cards')
        .select(`
          id,
          title,
          created_at,
          updated_at,
          assignee,
          kanban_columns (
            name,
            kanban_boards (
              name,
              user_id,
              clients (
                name
              )
            )
          )
        `)
        .eq('is_archived', false)
        .order('updated_at', { ascending: false })
        .limit(10);

      if (error) throw error;

      // Filter and format activities
      const userActivities = (data || []).filter(card => 
        card.kanban_columns?.kanban_boards?.user_id === user.id
      );

      const activities: RecentActivity[] = userActivities.map(card => {
        const isRecentlyCreated = new Date(card.created_at).getTime() > new Date(card.updated_at).getTime() - 1000;
        const userName = card.assignee || 'Sistema';
        
        return {
          id: card.id,
          action: isRecentlyCreated ? 'criou' : 'atualizou',
          card_title: card.title,
          board_name: card.kanban_columns?.kanban_boards?.name,
          client_name: card.kanban_columns?.kanban_boards?.clients?.name,
          user_name: userName,
          created_at: isRecentlyCreated ? card.created_at : card.updated_at,
          user: {
            name: userName,
            initials: userName.substring(0, 2).toUpperCase()
          },
          target: card.title,
          client: card.kanban_columns?.kanban_boards?.clients?.name,
          formattedTime: new Date(isRecentlyCreated ? card.created_at : card.updated_at).toLocaleString('pt-BR'),
          icon: isRecentlyCreated ? 'Plus' : 'Edit',
          iconColor: isRecentlyCreated ? 'text-green-500' : 'text-blue-500'
        };
      });

      setRecentActivities(activities);
    } catch (error: any) {
      console.error('Error fetching recent activities:', error);
      toast.error("Erro ao carregar atividades recentes");
    }
  };

  const getTasksByStatus = (status: 'pending' | 'in_progress' | 'completed') => {
    return tasks.filter(task => {
      const columnName = task.kanban_columns?.name?.toLowerCase();
      switch (status) {
        case 'pending':
          return columnName?.includes('fazer') || columnName?.includes('pendente') || columnName?.includes('backlog');
        case 'in_progress':
          return columnName?.includes('andamento') || columnName?.includes('fazendo') || columnName?.includes('progress');
        case 'completed':
          return columnName?.includes('concluí') || columnName?.includes('feito') || columnName?.includes('done');
        default:
          return false;
      }
    });
  };

  const getOverdueTasks = () => {
    const today = new Date();
    return tasks.filter(task => {
      if (!task.due_date) return false;
      const dueDate = new Date(task.due_date);
      return dueDate < today && !getTasksByStatus('completed').includes(task);
    });
  };

  const getUpcomingTasks = (days = 7) => {
    const today = new Date();
    const futureDate = new Date();
    futureDate.setDate(today.getDate() + days);
    
    return tasks.filter(task => {
      if (!task.due_date) return false;
      const dueDate = new Date(task.due_date);
      return dueDate >= today && dueDate <= futureDate && !getTasksByStatus('completed').includes(task);
    });
  };

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      await Promise.all([fetchTasks(), fetchRecentActivities()]);
      setIsLoading(false);
    };

    loadData();
  }, []);

  return {
    tasks,
    recentActivities,
    isLoading,
    fetchTasks,
    fetchRecentActivities,
    getTasksByStatus,
    getOverdueTasks,
    getUpcomingTasks,
    refreshTasks: fetchTasks,
    refreshActivities: fetchRecentActivities
  };
}

// Export the missing hooks
export function usePendingTasks() {
  const { getUpcomingTasks, isLoading } = useTasks();
  const tasks = getUpcomingTasks();
  
  const formattedTasks: TaskItem[] = tasks.map(task => ({
    id: task.id,
    title: task.title,
    dueDate: task.due_date || '',
    status: 'pending',
    client: task.kanban_columns?.kanban_boards?.clients?.name
  }));

  return {
    tasks: formattedTasks,
    isLoading,
    error: null
  };
}

export function useRecentActivities() {
  const { recentActivities, isLoading } = useTasks();
  
  return {
    activities: recentActivities,
    isLoading,
    error: null
  };
}
